/** Error with HTTP status code for typed catch blocks. */
export class StatusError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * Typed API layer for Supabase review sessions, comments, and approvals.
 *
 * Database schema (create these tables in Supabase dashboard):
 *
 *   review_sessions:
 *     id          uuid  PRIMARY KEY DEFAULT gen_random_uuid()
 *     project_json jsonb NOT NULL
 *     title       text  NOT NULL
 *     created_at  timestamptz DEFAULT now()
 *     expires_at  timestamptz NOT NULL
 *     revoked     boolean DEFAULT false
 *
 *   review_comments:
 *     id                uuid PRIMARY KEY DEFAULT gen_random_uuid()
 *     session_id        uuid REFERENCES review_sessions(id) ON DELETE CASCADE
 *     shot_id           integer
 *     timecode          real
 *     author            text NOT NULL
 *     text              text NOT NULL
 *     created_at        timestamptz DEFAULT now()
 *     resolved_at       timestamptz
 *     parent_comment_id uuid REFERENCES review_comments(id) ON DELETE CASCADE
 *     local_id          integer  -- matches the local Comment.id for dedup
 *
 *   review_approvals:
 *     id          uuid PRIMARY KEY DEFAULT gen_random_uuid()
 *     session_id  uuid REFERENCES review_sessions(id) ON DELETE CASCADE
 *     shot_id     integer NOT NULL
 *     status      text NOT NULL
 *     approved_by text NOT NULL
 *     approved_at timestamptz DEFAULT now()
 *     notes       text DEFAULT ''
 *     revision    integer DEFAULT 1
 *     local_id    integer  -- matches the local Approval.id for dedup
 */

import i18next from 'i18next';
import { getSupabaseForMode, invokeEdgeFn } from './supabaseClient.ts';
import type { RealtimeChannel } from '@supabase/supabase-js';

async function requireSupabase() {
  const sb = await getSupabaseForMode();
  if (!sb) throw new Error(i18next.t('errors:supabaseNotConfigured'));
  return sb;
}

// ── Row types (match Supabase table shape) ────────────────────────────

export interface ReviewSession {
  id: string;
  project_json: unknown;
  title: string;
  created_at: string;
  expires_at: string;
  revoked: boolean;
  revision?: number;
  is_creator?: boolean;
}

export interface ReviewCommentRow {
  id: string;
  session_id: string;
  shot_id: number | null;
  timecode: number | null;
  author: string;
  text: string;
  created_at: string;
  resolved_at: string | null;
  parent_comment_id: string | null;
  local_id: number | null;
  annotation_svg: string | null;
  screenplay_page: number | null;
  scene_id: number | null;
}

export interface ReviewApprovalRow {
  id: string;
  session_id: string;
  shot_id: number;
  status: string;
  approved_by: string;
  approved_at: string;
  notes: string;
  revision: number;
  local_id: number | null;
}

// ── Session metadata (returned by session_info) ──────────────────────

export interface SessionListItem {
  id: string;
  title: string;
  created_at: string;
  expires_at: string;
  revoked: boolean;
  comment_count: number;
  approval_count: number;
  revision: number;
  is_owner: boolean;
}

// ── Session CRUD ──────────────────────────────────────────────────────

export async function createSession(
  projectJson: unknown,
  title: string,
  ttlDays: number,
): Promise<ReviewSession> {
  const supabase = await requireSupabase();

  const expiresAt = new Date(Date.now() + ttlDays * 86400000).toISOString();

  const { data, error } = await supabase
    .from('review_sessions')
    .insert({ project_json: projectJson, title, expires_at: expiresAt })
    .select()
    .single();

  if (error) throw new Error(i18next.t('errors:failedToCreateSession', { message: error.message }));
  return data as ReviewSession;
}

export async function getSession(sessionId: string): Promise<ReviewSession | null> {
  const supabase = await requireSupabase();

  const { data, error } = await supabase
    .from('review_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // not found
    throw new Error(i18next.t('errors:failedToFetchSession', { message: error.message }));
  }

  const session = data as ReviewSession;

  // Check expiration and revocation
  if (session.revoked) return null;
  if (new Date(session.expires_at) < new Date()) return null;

  return session;
}

// ── Password-protected sessions ──────────────────────────────────────

/**
 * Create a review session with a generated password (via Edge Function).
 * OAuth session provides the JWT for the creator's session_access row.
 * Returns the session metadata + plaintext password.
 */
export async function createSessionWithPassword(
  projectJson: unknown,
  title: string,
  ttlDays: number,
): Promise<{ session: ReviewSession; password: string }> {
  const data = await invokeEdgeFn('validate-session', {
    action: 'create', project_json: projectJson, title, ttl_days: ttlDays,
  });

  const result = {
    session: data.session as ReviewSession,
    password: data.password as string,
  };

  // Fire-and-forget: claim reviewer link reward (one-time, server validates)
  import('@/api/rewardApi.ts')
    .then(({ recordReviewerLink }) => recordReviewerLink(result.session.id))
    .catch(() => {});

  return result;
}

/**
 * Validate a password for an existing session (via Edge Function).
 * On success, a session_access row is created and the session data (including
 * project_json) is returned so the client can load immediately.
 */
export async function validateSession(
  sessionId: string,
  password: string,
  reviewerName: string,
): Promise<ReviewSession> {
  try {
    const data = await invokeEdgeFn('validate-session', {
      action: 'validate', session_id: sessionId, password, reviewer_name: reviewerName,
    });
    return data.session as ReviewSession;
  } catch (err) {
    const msg = (err as Error).message;
    if (msg.includes('Incorrect password')) {
      throw new StatusError(msg, 403);
    }
    if (msg.includes('expired') || msg.includes('revoked')) {
      throw new StatusError(msg, 410);
    }
    throw err;
  }
}

// ── Session management (via Edge Function) ───────────────────────────

/**
 * List all sessions the current user has access to, with comment/approval counts.
 */
export async function getMySessionList(): Promise<SessionListItem[]> {
  const data = await invokeEdgeFn('validate-session', { action: 'session_info' });
  return (data.sessions ?? []) as SessionListItem[];
}

/**
 * Revoke a session (marks it as revoked, preventing further access).
 */
export async function revokeSession(sessionId: string): Promise<void> {
  await invokeEdgeFn('validate-session', { action: 'revoke', session_id: sessionId });
}

/**
 * Update a session's project_json and optionally extend its expiry.
 * Pass `expectedRevision` to enable optimistic locking — the server rejects
 * the update with a 409 if another write has bumped the revision.
 */
export async function updateSessionProject(
  sessionId: string,
  projectJson: unknown,
  extendDays?: number,
  expectedRevision?: number,
): Promise<{ revision: number }> {
  const body: Record<string, unknown> = {
    action: 'update',
    session_id: sessionId,
  };
  if (projectJson !== undefined) body.project_json = projectJson;
  if (extendDays) body.extend_days = extendDays;
  if (expectedRevision !== undefined) body.expected_revision = expectedRevision;

  const data = await invokeEdgeFn('validate-session', body);
  return { revision: data?.revision ?? 1 };
}

// ── Comments ──────────────────────────────────────────────────────────

export async function addComment(
  sessionId: string,
  shotId: number | null,
  timecode: number | null,
  author: string,
  text: string,
  localId: number,
  parentCommentId?: string | null,
  annotationSvg?: string | null,
  screenplayPage?: number | null,
  sceneId?: number | null,
): Promise<ReviewCommentRow> {
  const supabase = await requireSupabase();

  const { data, error } = await supabase
    .from('review_comments')
    .insert({
      session_id: sessionId,
      shot_id: shotId,
      timecode,
      author,
      text,
      local_id: localId,
      parent_comment_id: parentCommentId ?? null,
      annotation_svg: annotationSvg ?? null,
      screenplay_page: screenplayPage ?? null,
      scene_id: sceneId ?? null,
    })
    .select()
    .single();

  if (error) throw new Error(i18next.t('errors:failedToAddComment', { message: error.message }));
  return data as ReviewCommentRow;
}

export async function deleteComment(commentId: string): Promise<void> {
  const supabase = await requireSupabase();

  const { error } = await supabase
    .from('review_comments')
    .delete()
    .eq('id', commentId);

  if (error) throw new Error(i18next.t('errors:failedToDeleteComment', { message: error.message }));
}

export async function getSessionComments(sessionId: string): Promise<ReviewCommentRow[]> {
  const supabase = await requireSupabase();

  const { data, error } = await supabase
    .from('review_comments')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(i18next.t('errors:failedToFetchComments', { message: error.message }));
  return (data ?? []) as ReviewCommentRow[];
}

// ── Approvals ─────────────────────────────────────────────────────────

export async function setApproval(
  sessionId: string,
  shotId: number,
  status: string,
  approvedBy: string,
  localId: number,
  notes?: string,
  revision?: number,
): Promise<ReviewApprovalRow> {
  const supabase = await requireSupabase();

  const { data, error } = await supabase
    .from('review_approvals')
    .insert({
      session_id: sessionId,
      shot_id: shotId,
      status,
      approved_by: approvedBy,
      notes: notes ?? '',
      revision: revision ?? 1,
      local_id: localId,
    })
    .select()
    .single();

  if (error) throw new Error(i18next.t('errors:failedToSetApproval', { message: error.message }));
  return data as ReviewApprovalRow;
}

export async function getSessionApprovals(sessionId: string): Promise<ReviewApprovalRow[]> {
  const supabase = await requireSupabase();

  const { data, error } = await supabase
    .from('review_approvals')
    .select('*')
    .eq('session_id', sessionId)
    .order('approved_at', { ascending: true });

  if (error) throw new Error(i18next.t('errors:failedToFetchApprovals', { message: error.message }));
  return (data ?? []) as ReviewApprovalRow[];
}

// ── Realtime subscription ─────────────────────────────────────────────

export interface RealtimeCallbacks {
  onCommentInsert?: (row: ReviewCommentRow) => void;
  onCommentUpdate?: (row: ReviewCommentRow) => void;
  onCommentDelete?: (old: { id: string }) => void;
  onApprovalInsert?: (row: ReviewApprovalRow) => void;
}

export async function subscribeToSession(
  sessionId: string,
  callbacks: RealtimeCallbacks,
): Promise<RealtimeChannel | null> {
  const supabase = await getSupabaseForMode();
  if (!supabase) return null;

  const channel = supabase
    .channel(`review-${sessionId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'review_comments', filter: `session_id=eq.${sessionId}` },
      (payload) => callbacks.onCommentInsert?.(payload.new as ReviewCommentRow),
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'review_comments', filter: `session_id=eq.${sessionId}` },
      (payload) => callbacks.onCommentUpdate?.(payload.new as ReviewCommentRow),
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'review_comments', filter: `session_id=eq.${sessionId}` },
      (payload) => callbacks.onCommentDelete?.(payload.old as { id: string }),
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'review_approvals', filter: `session_id=eq.${sessionId}` },
      (payload) => callbacks.onApprovalInsert?.(payload.new as ReviewApprovalRow),
    )
    .subscribe();

  return channel;
}

export async function unsubscribe(channel: RealtimeChannel | null): Promise<void> {
  if (!channel) return;
  const supabase = await getSupabaseForMode();
  if (supabase) {
    supabase.removeChannel(channel);
  }
}
